
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { TranslationText } from "@/components/i18n/TranslationText";

export function SubscriptionsSettings() {
  const { toast } = useToast();
  const { getSetting, updateSetting, isLoading } = useSystemSettings();
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);

  // Load current setting
  useEffect(() => {
    const loadSetting = async () => {
      try {
        const settingValue = await getSetting("subscriptions_enabled", "true");
        setSubscriptionsEnabled(settingValue === "true");
      } catch (error) {
        console.error("Error loading subscription setting:", error);
        toast({
          title: "Error",
          description: "Failed to load subscription settings.",
          variant: "destructive",
        });
      } finally {
        setIsInitializing(false);
      }
    };

    loadSetting();
  }, [getSetting, toast]);

  const handleToggleSubscriptions = async (enabled: boolean) => {
    try {
      // Update UI state immediately for responsive feel
      setSubscriptionsEnabled(enabled);
      
      // Update the setting in the database
      const success = await updateSetting("subscriptions_enabled", enabled.toString());
      
      if (success) {
        toast({
          title: "Setting updated",
          description: `Subscriptions ${enabled ? "enabled" : "disabled"}.`,
        });
      } else {
        // Revert UI state if update failed
        setSubscriptionsEnabled(!enabled);
        toast({
          title: "Error",
          description: "Failed to update subscription setting.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Revert UI state if update failed
      setSubscriptionsEnabled(!enabled);
      console.error("Error updating subscription setting:", error);
      toast({
        title: "Error",
        description: "Failed to update subscription setting.",
        variant: "destructive",
      });
    }
  };

  if (isInitializing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <TranslationText i18nKey="admin.settings.subscriptions.title">
              Subscription Settings
            </TranslationText>
          </CardTitle>
          <CardDescription>
            <TranslationText i18nKey="admin.settings.subscriptions.loading">
              Loading settings...
            </TranslationText>
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <TranslationText i18nKey="admin.settings.subscriptions.title">
            Subscription Settings
          </TranslationText>
        </CardTitle>
        <CardDescription>
          <TranslationText i18nKey="admin.settings.subscriptions.description">
            Configure how subscriptions work in the application
          </TranslationText>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between space-x-2">
          <div className="space-y-0.5">
            <Label htmlFor="subscriptions-enabled">
              <TranslationText i18nKey="admin.settings.subscriptions.enable">
                Enable Subscriptions
              </TranslationText>
            </Label>
            <p className="text-sm text-muted-foreground">
              <TranslationText i18nKey="admin.settings.subscriptions.enableDescription">
                When disabled, all users will automatically get the free plan features
              </TranslationText>
            </p>
          </div>
          <Switch
            id="subscriptions-enabled"
            checked={subscriptionsEnabled}
            onCheckedChange={handleToggleSubscriptions}
            disabled={isLoading}
          />
        </div>
      </CardContent>
    </Card>
  );
}
